"use client";

import { useEffect, useState } from "react";

/** After this long the indicator says why it may take a while. */
const LONG_WORK_S = 60;
/** Before this the clock is noise — most replies start within a second or two. */
const CLOCK_AFTER_S = 3;

/** "12s" under a minute, "1:40" past it: reading "100s" costs the reader a beat. */
export function elapsedLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * Shown from the moment a message is sent until the first word streams in.
 * Says who is working and for how long, the way a terminal does, because a
 * tool call (a rebuild, a sheet read) can run for minutes with no text yet.
 *
 * A quiet line, not a message bubble: it is status, not something said. The
 * words shimmer rather than trailing three bouncing dots — one animation
 * instead of two saying the same thing, and the sentence stays readable.
 */
export default function WorkingIndicator({ name }: { name: string }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const id = setInterval(
      () => setSeconds(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    return () => clearInterval(id);
  }, []);
  const label = `${name} is working`;
  return (
    <div
      className="flex items-center gap-2 px-1 text-xs"
      role="status"
      aria-label={label}
    >
      <span className="caw-shimmer">{label}</span>
      {seconds >= CLOCK_AFTER_S && (
        <span className="rounded-md bg-zinc-800/80 px-1.5 py-0.5 text-[11px] text-zinc-400 tabular-nums">
          {elapsedLabel(seconds)}
          {seconds >= LONG_WORK_S && (
            <span className="text-zinc-500"> · rebuilds take a few minutes</span>
          )}
        </span>
      )}
    </div>
  );
}
